import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { LayoutDashboard, Wallet, CreditCard, Gift, User, Bell, Sparkles, LifeBuoy, Tag, Users, Gauge, LogOut, Menu, X, Trophy, BarChart3 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/i18n";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import WinterDecor from "@/components/WinterDecor";
import FeedbackWidget from "@/components/FeedbackWidget";
import InstallPWA from "@/components/InstallPWA";
import WelcomeAnimation from "@/components/WelcomeAnimation";
import NetworkStatus from "@/components/NetworkStatus";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/insights", label: "My Analytics", icon: BarChart3 },
  { to: "/loans", label: "My Loans", icon: Wallet },
  { to: "/payments", label: "Payments", icon: CreditCard },
  { to: "/cards", label: "Credit Card", icon: CreditCard },
  { to: "/wallet", label: "Wallet", icon: Wallet },
  { to: "/rewards", label: "Rewards", icon: Gift },
  { to: "/offers", label: "Offers", icon: Tag },
  { to: "/credit-score", label: "Credit Score", icon: Gauge },
  { to: "/ask-ai", label: "Ask AI", icon: Sparkles },
  { to: "/games", label: "Play & Earn", icon: Trophy },
  { to: "/refer", label: "Refer & Earn", icon: Users },
  { to: "/support", label: "Support", icon: LifeBuoy },
];
const bottomNav = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard },
  { to: "/loans", label: "Loans", icon: Wallet },
  { to: "/payments", label: "Pay", icon: CreditCard },
  { to: "/rewards", label: "Rewards", icon: Gift },
  { to: "/profile", label: "Profile", icon: User },
];
// Maps a nav route to its FeatureGate module key. A route is hidden ONLY when its feature is
// DISABLED (LIVE + MAINTENANCE stay visible). Routes not listed here are never feature-gated.
const NAV_MODULE = {
  "/rewards": "rewards", "/offers": "offers", "/support": "support",
  "/ask-ai": "ask_ai", "/games": "games_all", "/cards": "credit_card",
};

export default function CustomerShell() {
  const { user, logout } = useAuth();
  const t = useT();
  const loc = useLocation();
  const [logoutOpen, setLogoutOpen] = useState(false);
  const nvg = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [features, setFeatures] = useState({});

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  // Mobile drawer: lock background scroll & preserve position (Android Chrome + iOS Safari).
  useEffect(() => {
    if (!open) return;
    const y = window.scrollY;
    const b = document.body.style;
    b.position = "fixed"; b.top = `-${y}px`; b.left = "0"; b.right = "0"; b.width = "100%"; b.overflow = "hidden";
    return () => {
      b.position = ""; b.top = ""; b.left = ""; b.right = ""; b.width = ""; b.overflow = "";
      window.scrollTo(0, y);
    };
  }, [open]);
  useEffect(() => {
    api.get("/notifications").then((r) => { setNotifs(r.data); setUnread(r.data.filter((n) => !n.read).length); }).catch(() => {});
  }, [loc.pathname]);
  useEffect(() => { api.get("/features").then((r) => setFeatures(r.data || {})).catch(() => setFeatures({})); }, []);

  const active = (to) => loc.pathname === to || (to !== "/dashboard" && loc.pathname.startsWith(to));
  // DISABLED feature → hide its nav entry everywhere; MAINTENANCE/LIVE stay visible.
  const navVisible = (to) => {
    if (to === "/credit-score") return !!features.credit_score;
    const key = NAV_MODULE[to];
    if (!key) return true;
    return (features?.modules?.[key]?.status ?? "LIVE") !== "DISABLED";
  };

  return (
    <div className="min-h-screen bg-[#F7F5EF]">
      <WinterDecor />
      {/* Sidebar desktop */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col navy-grain lg:flex">
        <div className="flex h-16 items-center border-b border-white/10 px-6"><Logo light to="/dashboard" /></div>
        <nav className="flex-1 space-y-1 overflow-y-auto scroll-thin px-3 py-4">
          {nav.filter((n) => navVisible(n.to)).map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={cn("flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                active(n.to) ? "bg-[#14B8A6] text-white" : "text-slate-300 hover:bg-white/5 hover:text-white")}>
              <n.icon className="h-4.5 w-4.5" size={18} /> {t(n.label)}
            </Link>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <button onClick={() => setLogoutOpen(true)} data-testid="logout-btn"
            className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-slate-300 hover:bg-white/5 hover:text-white">
            <LogOut size={18} /> {t("Logout")}
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 overscroll-contain lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex h-[100dvh] w-72 flex-col overflow-y-auto overscroll-contain navy-grain p-4">
            <div className="mb-4 flex items-center justify-between"><Logo light to="/dashboard" /><button onClick={() => setOpen(false)} data-testid="drawer-close"><X className="text-white" /></button></div>
            <nav className="space-y-1 pb-[env(safe-area-inset-bottom)]">
              {nav.filter((n) => navVisible(n.to)).map((n) => (
                <Link key={n.to} to={n.to} className={cn("flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium",
                  active(n.to) ? "bg-[#14B8A6] text-white" : "text-slate-300")}><n.icon size={18} /> {t(n.label)}</Link>
              ))}
              <Link to="/profile" className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-slate-300"><User size={18} /> {t("Profile")}</Link>
              <button onClick={() => setLogoutOpen(true)} className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-slate-300"><LogOut size={18} /> {t("Logout")}</button>
            </nav>
          </aside>
        </div>
      )}

      <div className="relative z-10 lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/90 px-4 backdrop-blur-xl lg:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="menu-btn"><Menu className="text-[#0B132B]" /></button>
            <div className="lg:hidden"><Logo to="/dashboard" /></div>
            <div className="hidden lg:block">
              <p className="text-sm text-slate-500">{t("Welcome back,")}</p>
              <p className="-mt-0.5 font-display font-bold text-[#0B132B]">{user?.name || t("Customer")}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="notif-bell" className="relative grid h-9 w-9 place-items-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">
                  <Bell size={18} />
                  {unread > 0 && <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">{unread}</span>}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notif-dropdown">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
                  <span className="font-display text-sm font-bold text-[#0B132B]">{t("Notifications")}</span>
                  {unread > 0 && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-600">{unread} {t("new")}</span>}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifs.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-slate-400" data-testid="notif-empty"><Bell size={20} className="mx-auto mb-2 opacity-40" />{t("You're all caught up.")}</div>
                  ) : notifs.slice(0, 8).map((n) => (
                    <div key={n.id} className={cn("border-b border-slate-50 px-4 py-2.5 last:border-0", !n.read && "bg-teal-50/40")}>
                      <p className="text-sm font-medium text-[#0B132B]">{n.title || t("Notification")}</p>
                      {(n.message || n.body) && <p className="mt-0.5 text-xs text-slate-500">{n.message || n.body}</p>}
                    </div>
                  ))}
                </div>
                <Link to="/notifications" data-testid="notif-viewall" className="block border-t border-slate-100 px-4 py-2.5 text-center text-sm font-semibold text-[#14B8A6] hover:bg-slate-50">{t("View all")}</Link>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="header-avatar" title="Account" className="grid h-9 w-9 place-items-center rounded-full bg-[#0B132B] text-sm font-bold text-white transition-transform hover:scale-105">{(user?.name || "U")[0]}</button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64" data-testid="account-dropdown">
                <div className="flex items-center gap-3 px-2 py-2">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#0B132B] text-sm font-bold text-white">{(user?.name || "U")[0]}</span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-[#0B132B]" data-testid="account-name">{user?.name || t("Customer")}</p>
                    <p className="truncate text-xs text-slate-500" data-testid="account-email">{user?.email}</p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="account-profile"><Link to="/profile" className="cursor-pointer"><User size={15} className="mr-2" /> {t("Profile & Settings")}</Link></DropdownMenuItem>
                <DropdownMenuItem data-testid="account-logout" onClick={() => setLogoutOpen(true)} className="cursor-pointer text-red-600 focus:text-red-600"><LogOut size={15} className="mr-2" /> {t("Logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="mx-auto max-w-7xl px-4 pb-28 pt-6 lg:px-8 lg:pb-10">
          <Outlet />
        </main>
      </div>

      {/* Bottom nav mobile */}
      <nav style={{ paddingBottom: "calc(0.375rem + env(safe-area-inset-bottom))" }}
        className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-around border-t border-slate-200 bg-white/95 px-2 pt-1.5 backdrop-blur-xl lg:hidden">
        {bottomNav.filter((n) => navVisible(n.to)).map((n) => (
          <Link key={n.to} to={n.to} data-testid={`bottom-${n.label.toLowerCase()}`}
            className={cn("flex flex-1 flex-col items-center gap-0.5 rounded-md py-1.5 text-[11px] font-medium",
              active(n.to) ? "text-[#14B8A6]" : "text-slate-400")}>
            <n.icon size={20} /> {t(n.label)}
          </Link>
        ))}
      </nav>
      {(features?.modules?.feedback?.status ?? "LIVE") === "LIVE" && <FeedbackWidget />}
      <InstallPWA />
      <WelcomeAnimation />
      <NetworkStatus />
      <AlertDialog open={logoutOpen} onOpenChange={setLogoutOpen}>
        <AlertDialogContent data-testid="logout-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Logout?")}</AlertDialogTitle>
            <AlertDialogDescription>{t("Are you sure you want to logout from ZRo Pe?")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="logout-cancel">{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="logout-confirm" onClick={() => { flushSync(() => logout()); nvg("/", { replace: true }); }}>{t("Logout")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
